import CustomReusableModal from "@iewccouk/Components/CustomReusableModal";
import cancelQuote from "@iewccouk/Store/Handlers/CustomModals/CancelQuote";
import editQuote from "@iewccouk/Store/Handlers/CustomModals/EditQuote";
import placeOrder from "@iewccouk/Store/Handlers/CustomModals/PlaceOrder";
import requote from "@iewccouk/Store/Handlers/CustomModals/Requote";
import showHideCustomReusableModal from "@iewccouk/Store/Handlers/CustomModals/ShowHideCustomReusableModal";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
    quotesDataView: getQuotesDataView(state, state.pages.rfqMyQuotes.getQuotesParameter),
    rfqQuoteDetailsPageUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
    language: state.context.session.language,
});

const mapDispatchToProps = {
    showHideCustomReusableModal,
    placeOrder,
    requote,
    editQuote,
    cancelQuote,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqMyQuotesTableStyles {
    wrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    quoteNumberHeader?: DataTableHeaderProps;
    typeHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    orderDateHeader?: DataTableHeaderProps;
    expirationDateHeader?: DataTableHeaderProps;
    salesRepHeader?: DataTableHeaderProps;
    userHeader?: DataTableHeaderProps;
    customerHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    quoteNumberCell?: DataTableCellProps;
    quoteLink?: LinkPresentationProps;
    typeCell?: DataTableCellProps;
    statusCell?: DataTableCellProps;
    orderDateCell?: DataTableCellProps;
    expirationDateCell?: DataTableCellProps;
    salesRepCell?: DataTableCellProps;
    userCell?: DataTableCellProps;
    customerCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
    action?: any;

    /* Verndale Custom Feature - New styles */
    actionSelector?: SelectPresentationProps;
}

export const rfqMyQuotesTableStyles: RfqMyQuotesTableStyles = {
    wrapper: {
        css: css`
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
    customerCell: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 200px;
            `,
        },
    },
    shipToCell: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 200px;
            `,
        },
    },
    action: {
        css: css`
            position: relative;

            &::after {
                content: "...";
                pointer-events: none;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -65%);
            }
        `,
    },
};

const styles = rfqMyQuotesTableStyles;

const RfqMyQuotesTable = ({
    session,
    quotesDataView,
    rfqQuoteDetailsPageUrl,
    language,

    /* Verndale Custom Feature - New methods */
    showHideCustomReusableModal,
    placeOrder,
    requote,
    editQuote,
    cancelQuote,
}: Props) => {
    const quotes = quotesDataView.value;
    if (!quotes) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    if (quotes.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography {...styles.noResultsText}>{translate("No quotes found")}</Typography>
            </StyledWrapper>
        );
    }

    const handleAction = (key: string, data: any) => {
        showHideCustomReusableModal({
            orderNumber: data.id,
            key,
            show: true,
        });
    };

    const getStatus = (quote: any) => {
        return quote.status.split("Quote")[1] || quote.status;
    };

    const parseOrderType = (orderType = "") => {
        switch (orderType.toUpperCase()) {
            case "CARS":
                return translate("AIR Quote");
            case "EDI":
                return translate("EDI Quote");
            case "OCR":
                return translate("Sales Rep");
            case "WEB":
                return translate("Web Quote");
            case "SA":
                return translate("Scheduled Quote");
            default:
                return translate("Sales Rep");
        }
    };

    const getPrice = (quote: any) => {
        const status = getStatus(quote).toLowerCase();
        const price = quote.productTotalDisplay;
        if (status === "open") {
            return translate("Price Pending");
        }

        if (status === "expired") {
            return translate("Expired");
        }

        return price;
    };

    const rows = quotes.map((quote: any) => ({
        id: quote.id,
        quoteNumber: quote.erpOrderNumber || quote.webOrderNumber || translate("Pending"),
        channel: parseOrderType(quote.properties?.orderChannel),
        status: getStatus(quote),
        orderDate: quote.orderDate ? getLocalizedDateTime({ dateTime: new Date(quote.orderDate), language }) : "",
        expirationDate: quote.properties?.expirationDate
            ? getLocalizedDateTime({ dateTime: new Date(quote.properties.expirationDate), language })
            : "",
        salesRep: quote.salespersonName,
        price: getPrice(quote),
        customerPO: quote.customerPO,
        shipTo: quote.shipToFullAddress,
        numberOfItems: quote.properties.itemLines,
    }));

    return (
        <StyledWrapper {...styles.wrapper}>
            <DataTable {...styles.dataTable} data-test-selector="rfqMyQuotes_quoteTable">
                <DataTableHead {...styles.dataTableHead}>
                    <DataTableHeader {...styles.quoteNumberHeader} title={translate("Quote Number")}>
                        {translate("Quote #")}
                    </DataTableHeader>
                    <DataTableHeader {...styles.statusHeader}>{translate("Status")}</DataTableHeader>
                    <DataTableHeader {...styles.typeHeader}>{translate("Channel")}</DataTableHeader>
                    <DataTableHeader {...styles.customerHeader}>{translate("Customer P.O.")}</DataTableHeader>
                    <DataTableHeader {...styles.orderDateHeader}>{translate("Start date")}</DataTableHeader>
                    <DataTableHeader {...styles.expirationDateHeader}>{translate("Expire date")}</DataTableHeader>
                    <DataTableHeader>{translate("price")}</DataTableHeader>
                    <DataTableHeader>{translate("# of Items")}</DataTableHeader>
                    <DataTableHeader>{translate("Actions")}</DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.dataTableBody}>
                    {rows.map(
                        ({
                            id,
                            quoteNumber,
                            channel,
                            status,
                            orderDate,
                            expirationDate,
                            price,
                            customerPO,
                            shipTo,
                            numberOfItems,
                        }) => (
                            <DataTableRow
                                key={id}
                                {...styles.dataTableRow}
                                data-test-selector={`rfqMyQuotes_quoteLine_${id}`}
                            >
                                <DataTableCell {...styles.quoteNumberCell}>
                                    <Link
                                        {...styles.quoteLink}
                                        href={`${rfqQuoteDetailsPageUrl}?quoteId=${id}`}
                                        data-test-selector="link"
                                    >
                                        {quoteNumber}
                                    </Link>
                                </DataTableCell>
                                <DataTableCell {...styles.statusCell}>{status}</DataTableCell>
                                <DataTableCell {...styles.typeCell}>{channel}</DataTableCell>
                                <DataTableCell {...styles.typeCell}>{customerPO}</DataTableCell>
                                <DataTableCell {...styles.orderDateCell}>
                                    <Link
                                        {...styles.quoteLink}
                                        href={`${rfqQuoteDetailsPageUrl}?quoteId=${id}`}
                                        data-test-selector="link"
                                    >
                                        {orderDate}
                                    </Link>
                                </DataTableCell>
                                <DataTableCell {...styles.expirationDateCell}>{expirationDate}</DataTableCell>
                                <DataTableCell>{price}</DataTableCell>
                                <DataTableCell>{numberOfItems}</DataTableCell>
                                <DataTableCell>
                                    <StyledWrapper {...styles.action}>
                                        <Select
                                            {...styles.actionSelector}
                                            onChange={e => handleAction(e.target.value, { id, quoteNumber })}
                                            value=""
                                            autoComplete="off"
                                        >
                                            <option value=""></option>
                                            {status === "Open" && (
                                                <option value="edit-quote">
                                                    {translate("Edit Quote")}
                                                    &nbsp;&nbsp;&nbsp;
                                                </option>
                                            )}
                                            {status === "Open" && (
                                                <option value="cancel-quote">
                                                    {translate("Cancel Quote")}
                                                    &nbsp;&nbsp;&nbsp;
                                                </option>
                                            )}
                                            {status === "Completed" && (
                                                <option value="place-order">
                                                    {translate("Place Order")}
                                                    &nbsp;&nbsp;&nbsp;
                                                </option>
                                            )}
                                            {status === "Expired" && (
                                                <option value="requote">
                                                    {translate("Requote")}
                                                    &nbsp;&nbsp;&nbsp;
                                                </option>
                                            )}
                                        </Select>
                                    </StyledWrapper>
                                </DataTableCell>
                            </DataTableRow>
                        ),
                    )}
                </DataTableBody>
            </DataTable>

            <CustomReusableModal
                id="place-order"
                headline={translate("Place Order")}
                subHeadline={translate(
                    `Please provide a PO number and confirm your shipping address below. Click "YES" to confirm your order request.`,
                )}
                messageHint={translate("Enter additional information about your request")}
                submitLabel={translate("Yes")}
                cancelLabel={translate("No")}
                errorMessage={siteMessage("Change_Message_Error")}
                submitRequest={placeOrder}
            ></CustomReusableModal>

            <CustomReusableModal
                id="requote"
                headline={translate("Requote")}
                subHeadline={translate(
                    "Are you sure you would like a sales rep to re-quote all associated products and selections?",
                )}
                messageHint={translate("Enter additional information about your request")}
                submitLabel={translate("Yes")}
                cancelLabel={translate("Cancel")}
                errorMessage={siteMessage("Requote_Message_Error")}
                submitRequest={requote}
            ></CustomReusableModal>

            <CustomReusableModal
                id="edit-quote"
                headline={translate("Edit Quote")}
                subHeadline={translate("Why would you like to change this quote?")}
                dropdownHint={translate("Select a response")}
                messageHint={translate("Enter changes needed to your quote")}
                submitLabel={translate("Request Change")}
                cancelLabel={translate("Cancel")}
                errorMessage={siteMessage("Edit_Message_Error")}
                submitRequest={editQuote}
            ></CustomReusableModal>

            <CustomReusableModal
                id="cancel-quote"
                headline={translate("Cancel Quote")}
                subHeadline={translate("Why would you like to cancel this quote?")}
                dropdownHint={translate("Select a response")}
                messageHint={translate("Enter additional information about quote cancelation")}
                submitLabel={translate("Request Cancelation")}
                cancelLabel={translate("Cancel")}
                errorMessage={siteMessage("Cancel_Message_Error")}
                submitRequest={cancelQuote}
            ></CustomReusableModal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RfqMyQuotesTable as any) as any,
    definition: {
        group: "RFQ My Quotes",
        displayName: "Search Results Table",
        allowedContexts: ["RfqMyQuotesPage"],
    },
};

export default widgetModule;
